import React, { useState, useEffect, useRef } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Text,
    Icon,
    Link,
    Box,
    Input,
    FormControl,
    FormLabel,
    useDisclosure,
    Collapse,
    IconButton,
    Tooltip,
    ScaleFade,
    SlideFade,
    Heading,
} from '@chakra-ui/react';
import { FiMail, FiMessageCircle, FiHelpCircle } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { COLORS } from '../styles/colors';

const MotionBox = motion(Box);

export const HelpPopup = ({ 
    pageName = "this page",
    defaultDelay = 300000, // 5 minutes (300,000ms) default
    enableTestMode = false,
    contactEmail = "admin@edusphere.edu.sg",
    whatsappNumber = "+6580706203",
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isTyping, setIsTyping] = useState(false);
    const [timerDelay, setTimerDelay] = useState(defaultDelay);
    const [showConfig, setShowConfig] = useState(false);
    const [clickCount, setClickCount] = useState(0);
    const [hasAutoShown, setHasAutoShown] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [showFloatingButton, setShowFloatingButton] = useState(false);
    
    const timerRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const clickTimeoutRef = useRef(null);

    // Email and WhatsApp configuration
    const emailSubject = encodeURIComponent(`Help Request - ${pageName}`);
    const emailBody = encodeURIComponent(
        `Hi,\n\nI need assistance with ${pageName}.\n\n[Please describe your issue here]\n\nThank you!`
    );
    const whatsappMessage = encodeURIComponent(
        `Hi, I need help with ${pageName} on the Edusphere portal.`
    );

    // Detect typing in any input field
    useEffect(() => {
        const handleFocus = () => {
            setIsTyping(true);
            clearTimeout(typingTimeoutRef.current);
        };

        const handleBlur = () => {
            // Wait 1 second after blur to ensure user is done typing
            typingTimeoutRef.current = setTimeout(() => {
                setIsTyping(false);
            }, 1000);
        };

        const handleInput = () => {
            setIsTyping(true);
            clearTimeout(typingTimeoutRef.current);
            
            // Consider user not typing after 2 seconds of no input
            typingTimeoutRef.current = setTimeout(() => {
                setIsTyping(false);
            }, 2000);
        };

        // Add listeners to all input elements
        const inputs = document.querySelectorAll('input, textarea, select');
        inputs.forEach(input => {
            input.addEventListener('focus', handleFocus);
            input.addEventListener('blur', handleBlur);
            input.addEventListener('input', handleInput);
        });

        return () => {
            inputs.forEach(input => {
                input.removeEventListener('focus', handleFocus);
                input.removeEventListener('blur', handleBlur);
                input.removeEventListener('input', handleInput);
            });
            clearTimeout(typingTimeoutRef.current);
        };
    }, []);

    // Main timer logic
    useEffect(() => {
        if (hasAutoShown) return; // Only auto-show once per session

        const startTimer = () => {
            clearTimeout(timerRef.current);
            
            timerRef.current = setTimeout(() => {
                if (!isTyping && !hasAutoShown) {
                    onOpen();
                    setHasAutoShown(true);
                }
            }, timerDelay);
        };

        startTimer();

        return () => {
            clearTimeout(timerRef.current);
        };
    }, [timerDelay, isTyping, hasAutoShown, onOpen]);

    // Handle modal close with animation to floating button
    const handleClose = () => {
        setIsMinimized(true);
        onClose();
        
        // Show floating button after animation
        setTimeout(() => {
            setShowFloatingButton(true);
        }, 300);
    };

    // Handle floating button click
    const handleFloatingButtonClick = () => {
        setShowFloatingButton(false);
        setTimeout(() => {
            onOpen();
            setIsMinimized(false);
        }, 200);
    };

    // Handle title triple-click for test mode
    const handleTitleClick = () => {
        if (!enableTestMode) return;

        setClickCount(prev => prev + 1);
        
        // Reset click count after 2 seconds
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = setTimeout(() => {
            setClickCount(0);
        }, 2000);

        if (clickCount + 1 === 3) {
            setShowConfig(prev => !prev);
            setClickCount(0);
        }
    };

    const handleDelayChange = (e) => {
        const seconds = parseInt(e.target.value) || 10;
        setTimerDelay(seconds * 1000);
    };

    const handleTestNow = () => {
        setHasAutoShown(false);
        setShowFloatingButton(false);
        clearTimeout(timerRef.current);
        onOpen();
    };

    const handleReset = () => {
        setHasAutoShown(false);
        setIsTyping(false);
        setShowFloatingButton(false);
        setIsMinimized(false);
        clearTimeout(timerRef.current);
        onClose();
    };

    return (
        <>
            {/* Hidden config panel - shown after 3 clicks in test mode */}
            {enableTestMode && (
                <Box
                    position="fixed"
                    top={0}
                    left={0}
                    right={0}
                    zIndex={9999}
                    onClick={handleTitleClick}
                    cursor="pointer"
                    p={2}
                    bg="transparent"
                    _hover={{ bg: 'rgba(0,0,0,0.05)' }}
                >
                    <Text fontSize="xs" textAlign="center" color="gray.400">
                        Click here 3 times to configure timer
                    </Text>
                </Box>
            )}

            <Collapse in={showConfig} animateOpacity>
                <Box
                    position="fixed"
                    top={10}
                    right={4}
                    p={4}
                    bg="white"
                    borderRadius="md"
                    boxShadow="xl"
                    border="2px solid"
                    borderColor="orange.400"
                    zIndex={10000}
                    minW="300px"
                >
                    <VStack spacing={3} align="stretch">
                        <Text fontWeight="bold" color={COLORS.maastrichtBlue}>
                            🧪 Test Mode Configuration
                        </Text>
                        
                        <FormControl>
                            <FormLabel fontSize="sm">Timer Delay (seconds)</FormLabel>
                            <Input
                                type="number"
                                defaultValue={10}
                                onChange={handleDelayChange}
                                size="sm"
                                min={1}
                            />
                        </FormControl>

                        <HStack spacing={2}>
                            <Button
                                size="sm"
                                colorScheme="orange"
                                onClick={handleTestNow}
                                flex={1}
                            >
                                Test Now
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleReset}
                                flex={1}
                            >
                                Reset
                            </Button>
                        </HStack>

                        <Text fontSize="xs" color="gray.600">
                            Status: {isTyping ? '⌨️ User typing...' : '✅ Monitoring'}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                            Auto-shown: {hasAutoShown ? 'Yes' : 'Not yet'}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                            Floating button: {showFloatingButton ? 'Visible' : 'Hidden'}
                        </Text>
                    </VStack>
                </Box>
            </Collapse>

            {/* Floating Help Button - Smaller & Circular */}
            <AnimatePresence>
                {showFloatingButton && (
                    <MotionBox
                        position="fixed"
                        bottom={5}
                        right={5}
                        zIndex={9998}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    >
                        <Tooltip 
                            label="Need help?" 
                            placement="left"
                            hasArrow
                            bg={COLORS.maastrichtBlue}
                            fontSize="sm"
                        >
                            <Box
                                position="relative"
                                onClick={handleFloatingButtonClick}
                                cursor="pointer"
                            >
                                {/* Pulsing ring animation */}
                                <Box
                                    position="absolute"
                                    top="50%"
                                    left="50%"
                                    transform="translate(-50%, -50%)"
                                    width="60px"
                                    height="60px"
                                    borderRadius="full"
                                    bg={COLORS.mandarin}
                                    opacity={0.3}
                                    animation="pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
                                />
                                
                                {/* Main circular button - smaller */}
                                <Box
                                    bg={COLORS.mandarin}
                                    color="white"
                                    width="56px"
                                    height="56px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    borderRadius="full"
                                    boxShadow="xl"
                                    _hover={{
                                        transform: 'scale(1.1)',
                                        bg: '#D66A3A',
                                        boxShadow: '2xl',
                                    }}
                                    transition="all 0.2s"
                                >
                                    <Icon as={FiHelpCircle} boxSize={6} />
                                </Box>
                            </Box>
                        </Tooltip>
                    </MotionBox>
                )}
            </AnimatePresence>

            {/* Pulse animation keyframes */}
            <style>
                {`
                    @keyframes pulse {
                        0%, 100% {
                            transform: translate(-50%, -50%) scale(1);
                            opacity: 0.3;
                        }
                        50% {
                            transform: translate(-50%, -50%) scale(1.3);
                            opacity: 0;
                        }
                    }
                `}
            </style>

            {/* Help Modal - Redesigned */}
            <Modal 
                isOpen={isOpen} 
                onClose={handleClose} 
                size="lg" 
                isCentered
                motionPreset="slideInBottom"
            >
                <ModalOverlay 
                    bg="blackAlpha.400" 
                    backdropFilter="blur(8px)" 
                />
                <ModalContent
                    bg={COLORS.isabelline}
                    borderRadius="2xl"
                    overflow="hidden"
                    mx={4}
                >
                    {/* Header with gradient */}
                    <Box
                        bgGradient={`linear(to-r, ${COLORS.maastrichtBlue}, ${COLORS.shadowBlue})`}
                        p={6}
                        pb={8}
                        position="relative"
                    >
                        <ModalCloseButton 
                            color="white" 
                            _hover={{ bg: 'whiteAlpha.200' }}
                            top={4}
                            right={4}
                        />
                        
                        <VStack spacing={2} align="start">
                            <HStack spacing={3}>
                                <Box
                                    bg="white"
                                    p={2}
                                    borderRadius="lg"
                                    boxShadow="md"
                                >
                                    <Icon as={FiHelpCircle} boxSize={6} color={COLORS.mandarin} />
                                </Box>
                                <Heading size="lg" color="white">
                                    Need Help?
                                </Heading>
                            </HStack>
                            <Text color="whiteAlpha.900" fontSize="md">
                                Having trouble with {pageName}? We're here to assist you!
                            </Text>
                        </VStack>
                    </Box>
                    
                    <ModalBody p={6} pt={8}>
                        <VStack spacing={4} align="stretch">
                            {/* Email Option */}
                            <Link
                                href={`mailto:${contactEmail}?subject=${emailSubject}&body=${emailBody}`}
                                _hover={{ textDecoration: 'none' }}
                            >
                                <MotionBox
                                    whileHover={{ scale: 1.02, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <Box
                                        p={5}
                                        borderRadius="xl"
                                        bg="white"
                                        boxShadow="md"
                                        border="2px solid"
                                        borderColor="transparent"
                                        _hover={{
                                            borderColor: COLORS.shadowBlue,
                                            boxShadow: 'lg'
                                        }}
                                        transition="all 0.2s"
                                        cursor="pointer"
                                    >
                                        <HStack spacing={4}>
                                            <Box
                                                p={4}
                                                borderRadius="xl"
                                                bgGradient="linear(to-br, blue.400, blue.600)"
                                                color="white"
                                            >
                                                <Icon as={FiMail} boxSize={7} />
                                            </Box>
                                            <VStack align="start" spacing={1} flex={1}>
                                                <Text 
                                                    fontWeight="bold" 
                                                    fontSize="lg"
                                                    color={COLORS.maastrichtBlue}
                                                >
                                                    Send us an Email
                                                </Text>
                                                <Text fontSize="sm" color={COLORS.shadowBlue}>
                                                    {contactEmail}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    We'll respond within 24 hours
                                                </Text>
                                            </VStack>
                                        </HStack>
                                    </Box>
                                </MotionBox>
                            </Link>

                            {/* WhatsApp Option */}
                            <Link
                                href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${whatsappMessage}`}
                                isExternal
                                _hover={{ textDecoration: 'none' }}
                            >
                                <MotionBox
                                    whileHover={{ scale: 1.02, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <Box
                                        p={5}
                                        borderRadius="xl"
                                        bg="white"
                                        boxShadow="md"
                                        border="2px solid"
                                        borderColor="transparent"
                                        _hover={{
                                            borderColor: '#25D366',
                                            boxShadow: 'lg'
                                        }}
                                        transition="all 0.2s"
                                        cursor="pointer"
                                    >
                                        <HStack spacing={4}>
                                            <Box
                                                p={4}
                                                borderRadius="xl"
                                                bgGradient="linear(to-br, #25D366, #128C7E)"
                                                color="white"
                                            >
                                                <Icon as={FiMessageCircle} boxSize={7} />
                                            </Box>
                                            <VStack align="start" spacing={1} flex={1}>
                                                <Text 
                                                    fontWeight="bold" 
                                                    fontSize="lg"
                                                    color={COLORS.maastrichtBlue}
                                                >
                                                    Chat on WhatsApp
                                                </Text>
                                                <Text fontSize="sm" color={COLORS.shadowBlue}>
                                                    {whatsappNumber}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    Get instant support
                                                </Text>
                                            </VStack>
                                        </HStack>
                                    </Box>
                                </MotionBox>
                            </Link>
                        </VStack>
                    </ModalBody>

                    <ModalFooter bg="white" borderTop="1px solid" borderColor="gray.100">
                        <Button 
                            variant="ghost" 
                            onClick={handleClose}
                            color={COLORS.shadowBlue}
                            _hover={{ bg: 'gray.100' }}
                        >
                            I'm Good, Thanks!
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default HelpPopup;
